import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, setDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import {
  FaMinus, FaPlus, FaCopy, FaFilePdf, FaCheck, FaUndo, FaEye, FaRedo, FaSearch,
  FaCity, FaDoorOpen, FaAddressCard, FaArrowLeft,
} from "react-icons/fa";
import {
  colors, radius, spacing, typography, transitions, cardBase, inputBase,
  btnPrimary, btnSecondary, pageTitle, pageSubtitle,
} from "../../utils/designTokens";
import {
  PLAN_KEYS, PLAN_DEFAULT, PLANES_BO, EXTRAS_BO, CONTACTO_BO, VALIDEZ_DIAS_DEFAULT, IVA_LEYENDA,
  fmtU, cotizarBackoffice, rotuloPrecioUnidadBO, nroCotizacionBO, fechaLargaBO, fechaCortaBO, fechaVencimientoBO,
  resumenWhatsappBO, payloadCotizacionBO, inputsDesdeGuardadaBO, edificiosDesdeTexto,
} from "../../utils/planesBackoffice";
import { generarPresupuestoPdf } from "../../utils/presupuestoPdf";
import CotizacionBackofficeTemplate from "../../components/presupuesto/CotizacionBackofficeTemplate";
import AdminModal from "../../components/AdminModal";
import AppSelect from "../../components/AppSelect";
import EmptyState from "../../components/EmptyState";
import FormField from "../../components/FormField";
import StatusBadge from "../../components/StatusBadge";
import { useToast } from "../../components/Toast";
import { useUser } from "../../context/UserContext";
import { useCotizadorPrefill } from "../../context/CotizadorContext";
import { useAdministracionesData } from "../../hooks/useAdministracionesData";
import { useIsMobile } from "../../hooks/useResponsive";
import { computeBloqueadoHasta, puedeEditar } from "../../utils/crmAsignacion";

/**
 * Cotizador Back office (superadmin): arma la propuesta de UN plan (Plataforma,
 * Back office o Full Back office) para la cartera de una administradora, con el
 * total final ajustable a mano, y descarga el PDF "estilo Habitia" con todo lo
 * que incluye el plan (CotizacionBackofficeTemplate).
 *
 * Todo va "+ IVA": para la administradora el IVA no es un costo, así que acá no
 * se muestran montos con IVA ni el valor del IVA.
 *
 * Con la administradora vinculada al CRM (`administraciones`), "Guardar en la
 * ficha" deja la cotización y su historial en ese documento. También se llega
 * desde la ficha ("Cotizar Back office"): deja un prefill con origen
 * "administracion" en CotizadorContext, que se consume al montar.
 */

const SIN_VINCULAR = "";
// Estados del CRM que pasan a "Propuesta enviada" al guardar una cotización.
const ESTADOS_PREVIOS_A_PROPUESTA = ["sin_contactar", "contactado", "en_conversacion", "reunion"];
const HISTORIAL_MAX = 20; // entradas que se guardan en la ficha
const HISTORIAL_VISIBLE = 5; // las que se listan acá
// Hoja A4 a 96 dpi (210 × 297 mm), para escalar la vista previa.
const A4_W = 794;
const A4_H = 1123;
// Colores de plan del deck: coinciden con primary / primaryDark / primaryDarker.
const COLOR_PLAN = { plataforma: colors.primary, backoffice: colors.primaryDark, full: colors.primaryDarker };
// Layout: las columnas se deciden por el ancho disponible (el sidebar del
// superadmin mide 240 o 60 px), no por el viewport. Dos columnas solo si a la
// izquierda entran las tres tarjetas de plan en fila: 3 tarjetas + 2 huecos +
// el padding de la card, más el hueco entre columnas y la columna derecha.
const PLAN_MIN = 150; // ancho mínimo de una tarjeta de plan
const COL_DERECHA = 400;
const ANCHO_DOS_COLUMNAS = 3 * PLAN_MIN + 2 * spacing[3] + 2 * spacing[5] + spacing[5] + COL_DERECHA;

const FALTA_NOMBRE = "Escribí el nombre de la administradora para armar el PDF.";
const FALTA_NOMBRE_CORTO = "Falta el nombre de la administradora.";

// El template tiene 5 hojas: con memo no se redibuja por cambios que no tocan la propuesta.
const TemplateBO = React.memo(CotizacionBackofficeTemplate);

const enteroNoNegativo = (v) => Math.max(0, Math.floor(Number(v) || 0));
const miles = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const plural = (n, uno, varios) => `${miles(n)} ${n === 1 ? uno : varios}`;

/** "-$U 300" / "+$U 1.200": diferencia con signo explícito. */
const fmtDiferencia = (n) => `${n > 0 ? "+" : ""}${fmtU(n)}`;

/** "-6,67 %" / "+10 %": porcentaje con coma decimal y signo explícito. */
function fmtPct(n) {
  const v = Number(n) || 0;
  return `${v > 0 ? "+" : ""}${String(v).replace(".", ",")} %`;
}

/** Número de propuesta nuevo que no repite ninguno de `evitar` (los ya emitidos y el actual). */
function nroNuevo(evitar = []) {
  const usados = new Set(evitar);
  const base = nroCotizacionBO();
  const seq = Number(base.slice(-4));
  for (let i = 0; i < 10000; i += 1) {
    const n = i === 0 ? base : nroCotizacionBO(new Date(), (seq + i) % 10000);
    if (!usados.has(n)) return n;
  }
  return base;
}

/**
 * Ancho de contenido (sin padding) de un elemento, en vivo. null mientras no se
 * pudo medir (jsdom no tiene layout ni ResizeObserver): ahí decide el viewport.
 */
function useAnchoContenido(ref) {
  const [ancho, setAncho] = useState(null);
  // Primera medida antes de pintar, para no mostrar un frame con el layout equivocado.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || !window.getComputedStyle) return;
    const cs = window.getComputedStyle(el);
    const w = el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    if (w > 0) setAncho(Math.round(w));
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entradas) => {
      const w = entradas[0] && entradas[0].contentRect ? entradas[0].contentRect.width : NaN;
      if (Number.isFinite(w) && w > 0) setAncho(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return ancho;
}

/** Nombre apto para el archivo del PDF (mismo criterio que el Cotizador). */
const nombreParaArchivo = (s) =>
  String(s || "").replace(/[^\w\sáéíóúÁÉÍÓÚüÜñÑ-]/g, "").replace(/\s+/g, " ").trim();

/** Correo del PDF: el de quien cotiza si es de Habitia; si no, el general. */
function correoParaPdf(email) {
  const e = String(email || "").trim().toLowerCase();
  return /@habitia\.ai$/.test(e) ? e : CONTACTO_BO.email;
}

export default function CotizadorBackoffice({ setSelected }) {
  const isMobile = useIsMobile();
  const contRef = useRef(null);
  const anchoContenido = useAnchoContenido(contRef);
  // Sin medida (jsdom), el viewport; con medida, el ancho real que deja el sidebar.
  const dosColumnas = anchoContenido !== null ? anchoContenido >= ANCHO_DOS_COLUMNAS : !isMobile;
  const { showToast } = useToast();
  const { userType, userEmail, userName } = useUser();
  const { administraciones, loading: cargandoAdms, upsertLocal } = useAdministracionesData();
  const { prefill, clearPrefill } = useCotizadorPrefill();

  // Prefill de la ficha ("Cotizar Back office"): se toma al montar y se limpia,
  // así no queda pegado al volver a la pantalla. Un prefill de otro origen (el
  // del Cotizador de edificios) no es de acá: ni se lee ni se limpia.
  const [prefillAdm] = useState(() =>
    (prefill && prefill.origen === "administracion" && prefill.administracionId ? prefill : null));
  useEffect(() => {
    if (prefillAdm) clearPrefill();
  }, [prefillAdm, clearPrefill]);

  const emailUsr = String(userEmail || auth.currentUser?.email || "").trim();

  // ── Administradora ──
  const [vinculo, setVinculo] = useState(() => (prefillAdm ? String(prefillAdm.administracionId) : SIN_VINCULAR));
  const [busqAdm, setBusqAdm] = useState("");
  const [nombreLibre, setNombreLibre] = useState("");
  const [contacto, setContacto] = useState("");
  // Con prefill, el contacto y los edificios salen de la ficha apenas está en memoria.
  const [sugerirDesdeFicha, setSugerirDesdeFicha] = useState(() => !!prefillAdm);

  // ── Plan, cartera y precio ──
  const [plan, setPlan] = useState(PLAN_DEFAULT);
  const [edificios, setEdificios] = useState(0);
  const [edificiosSugeridos, setEdificiosSugeridos] = useState(false);
  const [unidades, setUnidades] = useState(0);
  const [precioUnidad, setPrecioUnidad] = useState(() => String(PLANES_BO[PLAN_DEFAULT].precioDesde));
  const [precioEditado, setPrecioEditado] = useState(false);
  const [habiLlamada, setHabiLlamada] = useState({ activo: false, monto: "" });
  const [totalManual, setTotalManual] = useState(null); // null: sigue al calculado
  const [mostrarListaTachada, setMostrarListaTachada] = useState(false);
  const [validezDias, setValidezDias] = useState(String(VALIDEZ_DIAS_DEFAULT));
  const [preparadoNombre, setPreparadoNombre] = useState(() => String(userName || "").trim() || CONTACTO_BO.nombre);
  const [preparadoEmail, setPreparadoEmail] = useState(() => correoParaPdf(userEmail));

  // ── Propuesta: número y fecha ──
  // Mientras no se emite (PDF, resumen copiado o guardado en la ficha), el número
  // es un borrador y no cambia mientras se escribe. Al emitir, el número queda
  // atado a esa propuesta (su "firma": administradora, contacto, plan, cartera,
  // montos, validez…): si después cambia algo es otra propuesta y lleva otro
  // número; si se vuelve a la misma (p. ej. a la administradora anterior con los
  // mismos datos), recupera el suyo. Así dos administradoras nunca reciben el
  // mismo número y el que queda en una ficha es el del PDF que recibió.
  const [borrador, setBorrador] = useState(() => ({ nro: nroCotizacionBO(), fecha: new Date() }));
  const [emitidas, setEmitidas] = useState([]); // [{ firma, nro, fecha, guardada }]
  const numerosRef = useRef(new Set()); // números ya usados en la sesión (borradores y emitidos): no se repiten

  // ── UI ──
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [errorNombre, setErrorNombre] = useState("");
  const nombreInputRef = useRef(null);
  const [copiado, setCopiado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const templateRef = useRef(null);
  const generandoRef = useRef(false);
  const guardandoRef = useRef(false);
  const copiadoTimerRef = useRef(null);
  useEffect(() => {
    const timer = copiadoTimerRef;
    return () => clearTimeout(timer.current);
  }, []);

  // ── Derivados ──
  const vinculada = vinculo !== SIN_VINCULAR;
  const adm = useMemo(
    () => (vinculada ? administraciones.find((a) => String(a.id) === vinculo) || null : null),
    [administraciones, vinculo, vinculada]
  );
  const nombrePrefill = prefillAdm && String(prefillAdm.administracionId) === vinculo ? String(prefillAdm.nombre || "") : "";
  const nombreAdm = String((vinculada ? adm?.nombre || nombrePrefill : nombreLibre) || "").trim();
  const planDef = PLANES_BO[plan];
  const extraDisponible = EXTRAS_BO.habiLlamada.disponibleEn.includes(plan);

  const cot = useMemo(() => cotizarBackoffice({
    plan, unidades, edificios, precioUnidad,
    extras: { habiLlamada },
    totalFinal: totalManual,
  }), [plan, unidades, edificios, precioUnidad, habiLlamada, totalManual]);

  // Un total final en cero, o que no llega a cubrir los extras, dejaría el precio
  // por unidad en cero o negativo: se avisa y no se arma el PDF, el resumen ni el guardado.
  let errorTotal = "";
  if (cot.valido && cot.totalFinal <= 0) {
    errorTotal = "El total mensual final tiene que ser mayor que cero.";
  } else if (cot.valido && cot.precioUnidadFinal <= 0) {
    errorTotal = cot.extrasTotal > 0
      ? `El total mensual final tiene que superar el extra (${fmtU(cot.extrasTotal)} por mes ${IVA_LEYENDA}): subilo o sacá el extra.`
      : "El total mensual final es demasiado bajo para la cartera: el precio por unidad queda en cero.";
  }
  const puedeEmitir = cot.valido && !errorTotal;
  const extraSinMonto = extraDisponible && habiLlamada.activo && !(Number(habiLlamada.monto) > 0);
  const conDescuento = cot.valido && cot.ajustado && cot.ajusteMonto < 0;
  // La casilla tacha el precio de LISTA: solo se ofrece con el precio por unidad
  // de lista (si se editó, lo tachado no sería la lista y chocaría con el "desde").
  const puedeTacharLista = conDescuento && Math.abs(cot.precioUnidad - cot.precioLista) < 0.005;
  const listaTachada = puedeTacharLista && mostrarListaTachada;
  const contactoPdf = contacto.trim();

  // "Firma" de la propuesta: todo lo que sale en el PDF salvo el número y la fecha.
  const firma = useMemo(() => JSON.stringify([
    vinculo, nombreAdm, contactoPdf, cot, String(validezDias), listaTachada, preparadoNombre.trim(), preparadoEmail.trim(),
  ]), [vinculo, nombreAdm, contactoPdf, cot, validezDias, listaTachada, preparadoNombre, preparadoEmail]);
  // Si esta misma propuesta ya se emitió, conserva su número y su fecha; si no, el borrador.
  const emitida = emitidas.find((e) => e.firma === firma) || null;
  const nro = emitida ? emitida.nro : borrador.nro;
  const fecha = emitida ? emitida.fecha : borrador.fecha;
  // Ya guardada en la ficha con este número: no se guarda dos veces.
  const yaGuardada = !!emitida && emitida.guardada;

  const validezHasta = useMemo(() => fechaVencimientoBO(fecha, validezDias), [fecha, validezDias]);

  const templateData = useMemo(() => ({
    nro,
    fecha,
    validezHasta,
    // Sin nombre, el template pone "Propuesta Habitia" (el PDF igual pide el nombre).
    administradora: contactoPdf ? { nombre: nombreAdm, contactoNombre: contactoPdf } : { nombre: nombreAdm },
    cotizacion: cot,
    preparadoPor: { nombre: preparadoNombre.trim(), email: preparadoEmail.trim() },
    mostrarListaTachada: listaTachada,
  }), [nro, fecha, validezHasta, nombreAdm, contactoPdf, cot, preparadoNombre, preparadoEmail, listaTachada]);

  useEffect(() => {
    if (!sugerirDesdeFicha || !adm) return;
    setSugerirDesdeFicha(false);
    setContacto(String(adm.contactoNombre || ""));
    if (edificios === 0) {
      const sugeridos = edificiosDesdeTexto(adm.nEdificios);
      setEdificios(sugeridos);
      setEdificiosSugeridos(sugeridos > 0);
    }
  }, [sugerirDesdeFicha, adm, edificios]);

  const opcionesAdm = useMemo(() => {
    const q = busqAdm.trim().toLowerCase();
    const lista = administraciones
      .filter((a) => String(a.id) === vinculo || !q || `${a.nombre || ""} ${a.ciudadZona || ""}`.toLowerCase().includes(q))
      .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"))
      .map((a) => ({ value: String(a.id), label: a.nombre || "Sin nombre" }));
    if (vinculada && !lista.some((o) => o.value === vinculo)) {
      lista.unshift({ value: vinculo, label: nombrePrefill || "Administración vinculada" });
    }
    return [{ value: SIN_VINCULAR, label: "Sin vincular (escribir el nombre)" }, ...lista];
  }, [administraciones, busqAdm, vinculo, vinculada, nombrePrefill]);

  const historial = useMemo(
    () => (Array.isArray(adm?.cotizacionesBackoffice)
      ? adm.cotizacionesBackoffice.filter((e) => e && typeof e === "object").slice(0, HISTORIAL_VISIBLE)
      : []),
    [adm]
  );

  // ── Acciones ──

  /** Borrador con número y fecha nuevos: el número no repite ninguno de los de la sesión. */
  const borradorNuevo = () => {
    numerosRef.current.add(borrador.nro);
    numerosRef.current.add(nro);
    const n = nroNuevo(numerosRef.current);
    numerosRef.current.add(n);
    return { nro: n, fecha: new Date() };
  };

  // Registra que la propuesta en pantalla salió (PDF, resumen copiado o ficha):
  // su número queda atado a su firma y el borrador pasa a otro número para lo
  // que se cotice después.
  const registrarEmision = (registro, { guardada = false } = {}) => {
    numerosRef.current.add(registro.nro);
    setEmitidas((prev) => {
      const i = prev.findIndex((e) => e.firma === registro.firma);
      if (i < 0) return [...prev, { ...registro, guardada }];
      if (!guardada || prev[i].guardada) return prev;
      return prev.map((e, j) => (j === i ? { ...e, guardada: true } : e));
    });
    const siguiente = borradorNuevo();
    setBorrador((b) => (b.nro === registro.nro ? siguiente : b));
  };

  const elegirAdministracion = (valor) => {
    const id = String(valor ?? "");
    if (id === vinculo) return; // la misma opción: no cambia nada
    setVinculo(id);
    setSugerirDesdeFicha(false);
    setErrorNombre("");
    setPdfError("");
    // Otra administradora es otra propuesta: número y fecha nuevos (si a esta ya
    // se le había emitido una con los mismos datos, la firma le devuelve la suya).
    setBorrador(borradorNuevo());
    // De la ficha anterior no queda nada en la propuesta: el contacto ("A la
    // atención de") es el de la ficha nueva, o ninguno si queda sin vincular.
    const a = id ? administraciones.find((x) => String(x.id) === id) || null : null;
    setContacto(String(a?.contactoNombre || ""));
    if (a && (edificios === 0 || edificiosSugeridos)) {
      // Los edificios se sugieren desde la ficha si el campo está vacío (o si
      // también venían sugeridos de otra administradora).
      const sugeridos = edificiosDesdeTexto(a.nEdificios);
      setEdificios(sugeridos);
      setEdificiosSugeridos(sugeridos > 0);
    } else if (!a && edificiosSugeridos) {
      // Sin ficha, los edificios sugeridos por la anterior se descartan (los cargados a mano quedan).
      setEdificios(0);
      setEdificiosSugeridos(false);
    }
  };

  // Al cambiar de plan, el precio por unidad pasa al de lista del nuevo plan,
  // salvo que se haya escrito uno a mano.
  const elegirPlan = (key) => {
    setPlan(key);
    if (!precioEditado) setPrecioUnidad(String(PLANES_BO[key].precioDesde));
  };

  const cambiarPrecio = (valor) => {
    setPrecioUnidad(valor);
    setPrecioEditado(String(valor).trim() !== "" && Number(valor) !== planDef.precioDesde);
  };

  const usarPrecioLista = () => {
    setPrecioUnidad(String(planDef.precioDesde));
    setPrecioEditado(false);
  };

  const usarGuardada = (entrada) => {
    const inp = inputsDesdeGuardadaBO(entrada);
    const extra = inp.extras.habiLlamada || { activo: false, monto: 0 };
    setPlan(inp.plan);
    setUnidades(inp.unidades);
    setEdificios(inp.edificios);
    setEdificiosSugeridos(false);
    setPrecioUnidad(String(inp.precioUnidad));
    setPrecioEditado(inp.precioUnidad !== PLANES_BO[inp.plan].precioDesde);
    setHabiLlamada({ activo: extra.activo, monto: extra.monto > 0 ? String(extra.monto) : "" });
    setTotalManual(inp.totalFinal === null ? null : String(inp.totalFinal));
    setValidezDias(String(inp.validezDias));
    setMostrarListaTachada(inp.mostrarListaTachada);
    setPdfError("");
    showToast(`Cargué los datos de ${entrada.nro || "la cotización"}. Revisalos antes de mandar la propuesta.`, "info");
  };

  const nuevaCotizacion = () => {
    setVinculo(SIN_VINCULAR);
    setBusqAdm("");
    setNombreLibre("");
    setContacto("");
    setSugerirDesdeFicha(false);
    setPlan(PLAN_DEFAULT);
    setEdificios(0);
    setEdificiosSugeridos(false);
    setUnidades(0);
    setPrecioUnidad(String(PLANES_BO[PLAN_DEFAULT].precioDesde));
    setPrecioEditado(false);
    setHabiLlamada({ activo: false, monto: "" });
    setTotalManual(null);
    setMostrarListaTachada(false);
    setValidezDias(String(VALIDEZ_DIAS_DEFAULT));
    // Cotización nueva: número nuevo aunque después se repitan los datos de una ya emitida.
    setEmitidas([]);
    setBorrador(borradorNuevo());
    setErrorNombre("");
    setPdfError("");
    setCopiado(false);
  };

  // Sin nombre no se arma el PDF: se marca el campo (y se lo enfoca) o, con la
  // administradora vinculada sin nombre en el CRM, se avisa cómo seguir.
  const avisarFaltaNombre = () => {
    if (vinculada) {
      setPdfError("La administradora vinculada no tiene nombre en el CRM: completalo en su ficha o elegí «Sin vincular».");
      return;
    }
    setErrorNombre(FALTA_NOMBRE);
    // Con la vista previa abierta el campo queda detrás del modal: el aviso sale en su pie.
    if (!previewOpen && nombreInputRef.current) nombreInputRef.current.focus();
  };

  const handlePdf = async () => {
    if (generandoRef.current || !puedeEmitir) return;
    if (!nombreAdm) {
      avisarFaltaNombre();
      return;
    }
    const registro = { firma, nro, fecha };
    generandoRef.current = true;
    setGenerating(true);
    setPdfError("");
    try {
      // Fuentes e imágenes listas antes de capturar (si no, html2canvas puede encimar glifos).
      if (typeof document !== "undefined" && document.fonts && document.fonts.ready) await document.fonts.ready;
      const imgs = templateRef.current ? Array.from(templateRef.current.querySelectorAll("img")) : [];
      await Promise.all(imgs.map((img) => (typeof img.decode === "function" ? img.decode().catch(() => {}) : null)));
      await new Promise((r) => setTimeout(r, 150));
      const archivo = `Propuesta Habitia Back office - ${nombreParaArchivo(nombreAdm) || "Administradora"} - ${nro}.pdf`;
      await generarPresupuestoPdf(templateRef.current, archivo, { download: true });
      registrarEmision(registro);
    } catch (err) {
      console.error("Error generando el PDF de la propuesta Back office:", err);
      setPdfError("No se pudo generar el PDF. Probá de nuevo.");
    } finally {
      generandoRef.current = false;
      setGenerating(false);
    }
  };

  const handleCopiar = async () => {
    if (!puedeEmitir) return;
    const registro = { firma, nro, fecha };
    const texto = resumenWhatsappBO({ cotizacion: cot, administradoraNombre: nombreAdm, nro, validezHasta });
    try {
      await navigator.clipboard.writeText(texto);
      registrarEmision(registro);
      setCopiado(true);
      clearTimeout(copiadoTimerRef.current);
      copiadoTimerRef.current = setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
      console.error("No se pudo copiar el resumen:", err);
      showToast("No se pudo copiar el resumen. Probá de nuevo.", "error");
    }
  };

  // Guarda la cotización en la ficha de la administradora (merge sobre
  // `administraciones/{id}`): la última, el historial (20 como máximo, la más
  // nueva primero), una nota de gestión y el estado "propuesta" si venía de
  // antes. Si está asignada, renueva el reloj de 60 días del titular; el
  // superadmin no se la asigna.
  const handleGuardar = async () => {
    if (guardandoRef.current || !adm || !puedeEmitir || yaGuardada) return;
    const registro = { firma, nro, fecha };
    guardandoRef.current = true;
    setSaving(true);
    try {
      const ahora = new Date();
      const ref = doc(db, "administraciones", String(adm.id));
      // Se parte de la ficha como está ahora en Firestore, no del cache de la
      // lista (puede tener horas): así no se pisan las notas, las cotizaciones
      // ni el estado que otro agente haya cambiado mientras tanto.
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("la administración ya no está en el CRM");
      const ficha = { ...snap.data(), id: adm.id };
      if (!puedeEditar(ficha, { email: emailUsr.toLowerCase(), userType })) {
        showToast(`Esta administración está asignada a ${ficha.asignadoA || "otro agente"}: la cotización no se guardó.`, "error");
        return;
      }
      const entrada = payloadCotizacionBO({
        cotizacion: cot, nro, fecha, validezDias, validezHasta, mostrarListaTachada: listaTachada, por: emailUsr,
      });
      const previas = Array.isArray(ficha.cotizacionesBackoffice) ? ficha.cotizacionesBackoffice : [];
      const notasPrevias = Array.isArray(ficha.notas) ? ficha.notas : [];
      const payload = {
        cotizacionBackoffice: entrada,
        cotizacionesBackoffice: [entrada, ...previas].slice(0, HISTORIAL_MAX),
        notas: [...notasPrevias, {
          texto: `Cotización Back office ${entrada.nro}: plan ${entrada.planNombre} · ${plural(entrada.unidades, "unidad", "unidades")} · ${fmtU(entrada.totalFinal)} por mes ${IVA_LEYENDA}`,
          autor: emailUsr,
          at: ahora.toISOString(),
        }],
        updatedAt: serverTimestamp(),
        updatedBy: emailUsr,
      };
      const pasaAPropuesta = ESTADOS_PREVIOS_A_PROPUESTA.includes(ficha.estado || "sin_contactar");
      if (pasaAPropuesta) payload.estado = "propuesta";
      if (ficha.asignadoEmail) {
        // Timestamp, como el resto de las escrituras de este campo (ficha, CRM).
        payload.ultimaGestionAt = Timestamp.fromDate(ahora);
        payload.bloqueadoHasta = computeBloqueadoHasta(ahora);
      }
      await setDoc(ref, payload, { merge: true });
      // El cache queda con la ficha recién leída más lo que se escribió.
      upsertLocal(adm.id, { ...ficha, ...payload, updatedAt: Timestamp.fromDate(ahora) });
      registrarEmision(registro, { guardada: true });
      showToast(
        pasaAPropuesta
          ? `Cotización ${entrada.nro} guardada en la ficha. Estado: Propuesta enviada.`
          : `Cotización ${entrada.nro} guardada en la ficha.`,
        "success"
      );
    } catch (err) {
      console.error("No se pudo guardar la cotización Back office:", err);
      showToast(err?.message ? `No se pudo guardar la cotización: ${err.message}` : "No se pudo guardar la cotización.", "error");
    } finally {
      guardandoRef.current = false;
      setSaving(false);
    }
  };

  const vence = fechaLargaBO(validezHasta);
  const carteraTexto = cot.edificios > 0
    ? `${plural(cot.edificios, "edificio", "edificios")} · ${plural(cot.unidades, "unidad", "unidades")}`
    : plural(cot.unidades, "unidad", "unidades");
  const hintEdificios = edificiosSugeridos
    ? "Sugerido desde la ficha del CRM"
    : adm?.nEdificios ? `En la ficha: ${adm.nEdificios}` : "Opcional";
  // Lo que impide armar el PDF, en el pie de la vista previa (junto al botón).
  const avisoVistaPrevia = errorTotal || pdfError || (errorNombre ? FALTA_NOMBRE_CORTO : "");

  return (
    <div ref={contRef} style={{ padding: isMobile ? spacing[4] : spacing[6], maxWidth: 1100, margin: "0 auto" }}>
      {/* ============ Encabezado ============ */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: spacing[4], flexWrap: "wrap", marginBottom: spacing[5] }}>
        <div>
          {prefillAdm && typeof setSelected === "function" && (
            <button type="button" onClick={() => setSelected("administraciones")} style={{ ...linkBtn, marginBottom: spacing[2] }}>
              <FaArrowLeft size={10} /> Administraciones
            </button>
          )}
          <h1 style={pageTitle}>Cotizador Back office</h1>
          <p style={{ ...pageSubtitle, marginTop: spacing[1] }}>Propuesta para administradoras, + IVA</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
            Propuesta Nº {nro} · {fechaCortaBO(fecha)}
          </span>
          <button type="button" style={btnChico} className="habitia-btn-secondary" onClick={nuevaCotizacion}>
            <FaRedo size={11} /> Nueva cotización
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: dosColumnas ? `minmax(0, 1fr) ${COL_DERECHA}px` : "minmax(0, 1fr)", gap: spacing[5], alignItems: "start" }}>
        {/* ============ Columna izquierda: datos de la propuesta ============ */}
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[4], minWidth: 0 }}>
          {/* 1 · Administradora */}
          <section style={cardBase} aria-labelledby="bo-sec-adm">
            <h2 id="bo-sec-adm" style={sectionTitle}>Administradora</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[3] }}>
              <FormField label="Buscar en el CRM" htmlFor="bo-buscar">
                <div style={{ position: "relative" }}>
                  <FaSearch size={12} color={colors.gray400} aria-hidden="true" style={{ position: "absolute", left: spacing[3], top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                  <input
                    id="bo-buscar" type="search" className="habitia-input" autoComplete="off"
                    style={{ ...inputStyle, height: 42, paddingLeft: spacing[8] }}
                    placeholder="Nombre o zona" value={busqAdm} onChange={(e) => setBusqAdm(e.target.value)}
                  />
                </div>
              </FormField>
              <FormField label="Administradora" hint={cargandoAdms ? "Cargando administraciones…" : undefined}>
                <AppSelect value={vinculo} onChange={elegirAdministracion} options={opcionesAdm} ariaLabel="Administradora" />
              </FormField>
            </div>

            {vinculada ? (
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap", marginTop: spacing[3], fontSize: typography.sizes.sm, color: colors.gray600 }}>
                {adm && <StatusBadge status={adm.estado || "sin_contactar"} size="sm" />}
                <span>{adm ? "Vinculada al CRM: al guardar, la propuesta queda en su ficha." : "Cargando la ficha…"}</span>
                {adm?.asignadoA && <span>· Asignada a {adm.asignadoA}</span>}
              </div>
            ) : (
              <FormField label="Nombre de la administradora" htmlFor="bo-nombre" required error={errorNombre} style={{ marginTop: spacing[3] }}>
                <input
                  ref={nombreInputRef}
                  id="bo-nombre" type="text" className="habitia-input"
                  style={{ ...inputStyle, border: `1px solid ${errorNombre ? colors.error : colors.gray200}` }}
                  aria-invalid={errorNombre ? true : undefined}
                  placeholder="Ej. Gasalla Inmobiliaria" value={nombreLibre}
                  onChange={(e) => {
                    setNombreLibre(e.target.value);
                    if (errorNombre) setErrorNombre("");
                  }}
                />
              </FormField>
            )}

            <FormField label="Contacto (opcional)" htmlFor="bo-contacto" hint="La persona a la que va dirigida la propuesta." style={{ marginTop: spacing[3] }}>
              <input
                id="bo-contacto" type="text" className="habitia-input" style={inputStyle}
                placeholder="Ej. Pía" value={contacto} onChange={(e) => setContacto(e.target.value)}
              />
            </FormField>
          </section>

          {/* 2 · Plan */}
          <section style={cardBase}>
            <fieldset style={fieldsetReset}>
              <legend style={{ ...sectionTitle, padding: 0 }}>Plan</legend>
              {/* Las tarjetas bajan de fila cuando no entran (nunca se encima una con la columna derecha). */}
              <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${PLAN_MIN}px, 1fr))`, gap: spacing[3] }}>
                {PLAN_KEYS.map((key) => (
                  <PlanOpcion key={key} plan={PLANES_BO[key]} checked={plan === key} onSelect={() => elegirPlan(key)} />
                ))}
              </div>
            </fieldset>
            <p style={{ ...textoSecundario, margin: `${spacing[3]}px 0 0` }}>{planDef.resumen}</p>
          </section>

          {/* 3 · Cartera */}
          <section style={cardBase} aria-labelledby="bo-sec-cartera">
            <h2 id="bo-sec-cartera" style={sectionTitle}>Cartera</h2>
            <Stepper
              id="bo-edificios" icon={FaCity} label="Edificios" hint={hintEdificios} value={edificios}
              onChange={(v) => { setEdificios(enteroNoNegativo(v)); setEdificiosSugeridos(false); }}
            />
            <Stepper
              id="bo-unidades" icon={FaDoorOpen} label="Unidades" hint="Se cotiza por unidad, por mes" value={unidades}
              onChange={(v) => setUnidades(enteroNoNegativo(v))} required
            />
          </section>

          {/* 4 · Precio */}
          <section style={cardBase} aria-labelledby="bo-sec-precio">
            <h2 id="bo-sec-precio" style={sectionTitle}>Precio</h2>
            <FormField label="Precio por unidad (+ IVA)" htmlFor="bo-precio" hint={`Lista de ${planDef.nombre}: ${fmtU(planDef.precioDesde)} por unidad · mes ${IVA_LEYENDA}`}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
                <input
                  id="bo-precio" type="number" min="0" step="any" inputMode="decimal" className="habitia-input"
                  style={{ ...inputStyle, maxWidth: 180 }} value={precioUnidad}
                  onChange={(e) => cambiarPrecio(e.target.value)}
                  onBlur={() => {
                    // Vacío o negativo: el cálculo ya usa la lista, que el input lo muestre.
                    if (String(precioUnidad).trim() === "" || Number(precioUnidad) < 0) usarPrecioLista();
                  }}
                />
                {precioEditado && (
                  <button type="button" onClick={usarPrecioLista} style={linkBtn}>
                    <FaUndo size={10} /> Usar el de lista
                  </button>
                )}
              </div>
            </FormField>

            <div style={{ marginTop: spacing[4], paddingTop: spacing[4], borderTop: `1px solid ${colors.gray100}` }}>
              <div style={{ fontSize: typography.sizes.base, color: colors.gray700, marginBottom: spacing[2] }}>Extra</div>
              {extraDisponible ? (
                <>
                  <label htmlFor="bo-extra-llamada" style={checkLabel}>
                    <input
                      id="bo-extra-llamada" type="checkbox" checked={habiLlamada.activo} style={checkInput}
                      onChange={(e) => {
                        const activo = e.target.checked;
                        setHabiLlamada((x) => ({ ...x, activo }));
                      }}
                    />
                    {EXTRAS_BO.habiLlamada.label}
                  </label>
                  {habiLlamada.activo && (
                    <FormField
                      label="Monto mensual del extra (+ IVA)" htmlFor="bo-extra-monto" style={{ marginTop: spacing[3] }}
                      hint={extraSinMonto
                        ? "Cargá el monto: sin monto, el extra no entra en la propuesta."
                        : `${EXTRAS_BO.habiLlamada.nota} Se suma al total, aparte del precio por unidad.`}
                    >
                      <input
                        id="bo-extra-monto" type="number" min="0" step="any" inputMode="decimal" className="habitia-input"
                        style={{ ...inputStyle, maxWidth: 180 }} placeholder="Ej. 3000" value={habiLlamada.monto}
                        onChange={(e) => {
                          const monto = e.target.value;
                          setHabiLlamada((x) => ({ ...x, monto }));
                        }}
                      />
                    </FormField>
                  )}
                </>
              ) : (
                <p style={{ ...textoSecundario, margin: 0 }}>En Full Back office, Habi por llamada ya viene incluido.</p>
              )}
            </div>
          </section>

          {/* 5 · Validez y contacto del PDF */}
          <section style={cardBase} aria-labelledby="bo-sec-validez">
            <h2 id="bo-sec-validez" style={sectionTitle}>Validez y contacto</h2>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${PLAN_MIN}px, 1fr))`, gap: spacing[3] }}>
              <FormField label="Validez (días)" htmlFor="bo-validez" hint={vence ? `Vence el ${vence}` : undefined}>
                <input
                  id="bo-validez" type="number" min="1" step="1" inputMode="numeric" className="habitia-input"
                  style={inputStyle} value={validezDias} onChange={(e) => setValidezDias(e.target.value)}
                  onBlur={() => { if (String(validezDias).trim() === "") setValidezDias(String(VALIDEZ_DIAS_DEFAULT)); }}
                />
              </FormField>
              <FormField label="Contacto en el PDF" htmlFor="bo-prep-nombre">
                <input
                  id="bo-prep-nombre" type="text" className="habitia-input" style={inputStyle}
                  placeholder={CONTACTO_BO.nombre} value={preparadoNombre} onChange={(e) => setPreparadoNombre(e.target.value)}
                />
              </FormField>
              <FormField label="Correo en el PDF" htmlFor="bo-prep-email">
                <input
                  id="bo-prep-email" type="email" className="habitia-input" style={inputStyle}
                  placeholder={CONTACTO_BO.email} value={preparadoEmail} onChange={(e) => setPreparadoEmail(e.target.value)}
                />
              </FormField>
            </div>
          </section>
        </div>

        {/* ============ Columna derecha: resultado en vivo ============ */}
        <section
          aria-labelledby="bo-sec-cotizacion"
          style={{
            ...cardBase,
            position: dosColumnas ? "sticky" : "static",
            top: spacing[5],
            ...(dosColumnas ? { maxHeight: "calc(100vh - 96px)", overflowY: "auto" } : {}),
          }}
        >
          <h2 id="bo-sec-cotizacion" style={sectionTitle}>Cotización</h2>

          {!cot.valido ? (
            <EmptyState icon={FaCity} title="Cargá la cartera" description="Ingresá las unidades para cotizar." />
          ) : (
            <>
              <div>
                <div style={{ fontSize: 32, color: colors.primary, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                  <span>{fmtU(cot.totalFinal)}</span>{" "}
                  <span style={{ fontSize: typography.sizes.md, color: colors.gray600 }}>{IVA_LEYENDA}</span>
                </div>
                <div style={{ ...textoSecundario, marginTop: spacing[1] }}>
                  Total mensual · Plan {cot.planNombre} · {carteraTexto}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: spacing[1], margin: `${spacing[4]}px 0` }}>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Desglose por mes, {IVA_LEYENDA}</div>
                <Linea label={`${plural(cot.unidades, "unidad", "unidades")} × ${fmtU(cot.precioUnidad)}`} valor={fmtU(cot.subtotalUnidades)} />
                {cot.extras.map((x) => (
                  <Linea key={x.key} label={x.label} valor={fmtU(x.monto)} />
                ))}
                <Linea label="Total calculado" valor={`${fmtU(cot.totalCalculado)} ${IVA_LEYENDA}`} total />
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: spacing[2], marginBottom: spacing[1] }}>
                <label htmlFor="bo-total" style={labelStyle}>Total mensual final (+ IVA)</label>
                {totalManual !== null && (
                  <button type="button" onClick={() => setTotalManual(null)} style={linkBtn}>
                    <FaUndo size={10} /> Usar el calculado
                  </button>
                )}
              </div>
              <input
                id="bo-total" type="number" min="0" step="any" inputMode="decimal" className="habitia-input"
                style={inputStyle}
                value={totalManual === null ? String(cot.totalCalculado) : totalManual}
                onChange={(e) => setTotalManual(e.target.value)}
                onBlur={() => {
                  // Vacío o negativo: vuelve a seguir al calculado (que es lo que ya usa el cálculo).
                  if (totalManual !== null && (totalManual.trim() === "" || Number(totalManual) < 0)) setTotalManual(null);
                }}
              />
              {cot.ajustado && (
                <div style={{ marginTop: spacing[2] }}>
                  <InfoPill bg={cot.ajusteMonto < 0 ? colors.warningLight : colors.primaryLighter} color={cot.ajusteMonto < 0 ? colors.warningDark : colors.primary}>
                    Ajuste sobre el calculado: {fmtDiferencia(cot.ajusteMonto)} ({fmtPct(cot.ajustePct)})
                  </InfoPill>
                </div>
              )}
              {errorTotal && (
                <div style={{ marginTop: spacing[2] }} role="alert">
                  <InfoPill bg={colors.errorLight} color={colors.errorDark}>{errorTotal}</InfoPill>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: spacing[3], marginTop: spacing[4] }}>
                {/* "Precio promedio por unidad" si no reproduce el total al centésimo (igual que el PDF y el resumen). */}
                <span style={{ fontSize: typography.sizes.base, color: colors.gray600 }}>{rotuloPrecioUnidadBO(cot)}</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: typography.sizes.xl, color: colors.black }}>{fmtU(cot.precioUnidadFinal)}</span>{" "}
                  <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>por mes {IVA_LEYENDA}</span>
                </span>
              </div>
              {cot.debajoDelPiso && !errorTotal && (
                <div style={{ marginTop: spacing[2] }}>
                  <InfoPill bg={colors.warningLight} color={colors.warningDark}>
                    Por debajo de la lista de {cot.planNombre} ({fmtU(cot.precioLista)} por unidad · mes {IVA_LEYENDA}).
                  </InfoPill>
                </div>
              )}
              {puedeTacharLista && (
                <label htmlFor="bo-lista-tachada" style={{ ...checkLabel, fontSize: typography.sizes.base, color: colors.gray700, marginTop: spacing[3] }}>
                  <input
                    id="bo-lista-tachada" type="checkbox" checked={mostrarListaTachada} style={checkInput}
                    onChange={(e) => setMostrarListaTachada(e.target.checked)}
                  />
                  Mostrar el precio de lista tachado
                </label>
              )}
              {conDescuento && !puedeTacharLista && (
                <p style={{ ...hintStyle, marginTop: spacing[3] }}>
                  La lista tachada se ofrece solo con el precio por unidad de lista.
                </p>
              )}

              <p style={{ ...textoSecundario, margin: `${spacing[3]}px 0 0` }}>Válida hasta el {vence}</p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[2], marginTop: spacing[4] }}>
                <button
                  type="button" onClick={handlePdf} disabled={generating || !puedeEmitir} className="habitia-btn-primary"
                  style={{ ...btnPrimary, justifyContent: "center", gridColumn: "1 / -1", opacity: generating || !puedeEmitir ? 0.7 : 1 }}
                >
                  <FaFilePdf /> {generating ? "Generando…" : "Descargar PDF"}
                </button>
                <button type="button" onClick={() => setPreviewOpen(true)} className="habitia-btn-secondary" style={{ ...btnSecondary, justifyContent: "center", padding: "10px 12px" }}>
                  <FaEye /> Vista previa
                </button>
                <button
                  type="button" onClick={handleCopiar} disabled={!puedeEmitir} aria-label="Copiar resumen para WhatsApp" className="habitia-btn-secondary"
                  style={{ ...btnSecondary, justifyContent: "center", padding: "10px 12px", opacity: puedeEmitir ? 1 : 0.7 }}
                >
                  {copiado ? <FaCheck color={colors.success} /> : <FaCopy />} {copiado ? "Copiado" : "Copiar resumen"}
                </button>
                {vinculada && (
                  <button
                    type="button" onClick={handleGuardar} disabled={saving || !adm || yaGuardada || !puedeEmitir} className="habitia-btn-secondary"
                    style={{ ...btnSecondary, justifyContent: "center", gridColumn: "1 / -1", opacity: saving || !adm || !puedeEmitir ? 0.7 : 1, cursor: yaGuardada ? "default" : "pointer" }}
                  >
                    {yaGuardada ? <FaCheck color={colors.success} /> : <FaAddressCard />}{" "}
                    {yaGuardada ? "Guardada en la ficha" : saving ? "Guardando…" : "Guardar en la ficha"}
                  </button>
                )}
              </div>
              {(pdfError || errorNombre) && (
                // El aviso del nombre ya lo anuncia el campo (FormField con role="alert"): acá se repite a la vista.
                <div style={{ marginTop: spacing[2] }} role={pdfError ? "alert" : undefined}>
                  <InfoPill bg={colors.errorLight} color={colors.errorDark}>{pdfError || FALTA_NOMBRE_CORTO}</InfoPill>
                </div>
              )}
              {!vinculada && (
                <p style={{ ...hintStyle, marginTop: spacing[3] }}>
                  Para guardarla en el CRM, elegí la administradora en la lista.
                </p>
              )}
            </>
          )}

          {historial.length > 0 && (
            <div style={{ marginTop: spacing[5], paddingTop: spacing[4], borderTop: `1px solid ${colors.gray100}` }}>
              <div style={{ fontSize: typography.sizes.base, color: colors.gray700, marginBottom: spacing[2] }}>
                Cotizaciones anteriores
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[2] }}>
                {historial.map((e, i) => (
                  <li key={`${e.nro || "cot"}-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing[2], padding: `${spacing[2]}px ${spacing[3]}px`, background: colors.gray50, borderRadius: radius.sm }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: typography.sizes.base, color: colors.gray900 }}>
                        {fmtU(e.totalFinal)} {IVA_LEYENDA} · {e.planNombre || PLANES_BO[e.plan]?.nombre || ""}
                      </div>
                      <div style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>
                        {[fechaCortaBO(e.at), e.nro].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <button type="button" onClick={() => usarGuardada(e)} style={{ ...linkBtn, flexShrink: 0 }} aria-label={`Usar los datos de ${e.nro || "esta cotización"}`}>
                      Usar estos datos
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>

      {/* Vista previa: las hojas del PDF escaladas al ancho del modal */}
      <AdminModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        size="wide"
        title="Vista previa"
        subtitle={`Propuesta Nº ${nro} · así sale el PDF`}
        icon={<FaEye size={16} />}
        footer={(
          // El aviso va junto al botón: arriba del cuerpo quedaba fuera de vista al scrollear las hojas.
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: spacing[3], width: "100%" }}>
            {avisoVistaPrevia && (
              <div role="alert" style={{ flex: "1 1 240px", minWidth: 0 }}>
                <InfoPill bg={colors.errorLight} color={colors.errorDark}>{avisoVistaPrevia}</InfoPill>
              </div>
            )}
            <button type="button" onClick={() => setPreviewOpen(false)} style={btnSecondary} className="habitia-btn-secondary">Cerrar</button>
            <button type="button" onClick={handlePdf} disabled={generating || !puedeEmitir} style={{ ...btnPrimary, opacity: generating || !puedeEmitir ? 0.7 : 1 }} className="habitia-btn-primary">
              <FaFilePdf /> {generating ? "Generando…" : "Descargar PDF"}
            </button>
          </div>
        )}
      >
        {previewOpen && cot.valido && <VistaPreviaEscalada data={templateData} />}
      </AdminModal>

      {/* Template offscreen para la captura del PDF (html2canvas necesita el DOM a tamaño real) */}
      {cot.valido && (
        <div aria-hidden="true" style={{ position: "absolute", left: -20000, top: 0 }}>
          <TemplateBO ref={templateRef} data={templateData} />
        </div>
      )}
    </div>
  );
}

/** Tarjeta seleccionable de un plan (radio nativo accesible, oculto a la vista). */
function PlanOpcion({ plan, checked, onSelect }) {
  const [foco, setFoco] = useState(false);
  const color = COLOR_PLAN[plan.key] || colors.primary;
  const idDetalle = `bo-plan-${plan.key}-detalle`;
  return (
    <label
      className="habitia-card-hover"
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: spacing[1],
        minWidth: 0,
        padding: spacing[4],
        borderRadius: radius.md,
        border: `1px solid ${checked ? colors.primary : colors.gray200}`,
        background: checked ? colors.primarySubtle : colors.white,
        boxShadow: foco ? `0 0 0 3px ${colors.primaryLighter}` : "none",
        cursor: "pointer",
        transition: `all ${transitions.normal}`,
      }}
    >
      <input
        type="radio" name="bo-plan" value={plan.key} checked={checked} onChange={onSelect}
        onFocus={() => setFoco(true)} onBlur={() => setFoco(false)} aria-label={plan.nombre} aria-describedby={idDetalle} style={srOnly}
      />
      <NivelBarras nivel={plan.nivel} color={color} />
      <span style={{ fontSize: typography.sizes.lg, color: checked ? colors.primary : colors.black, marginTop: spacing[1] }}>{plan.nombre}</span>
      <span id={idDetalle} style={{ display: "flex", flexDirection: "column", gap: spacing[1] }}>
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{plan.kicker}</span>
        <span style={{ fontSize: typography.sizes.md, color: colors.gray900, marginTop: spacing[2], whiteSpace: "nowrap" }}>desde {fmtU(plan.precioDesde)}</span>
        <span style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>por unidad · mes {IVA_LEYENDA}</span>
      </span>
      {checked && (
        <FaCheck size={12} color={colors.primary} aria-hidden="true" style={{ position: "absolute", top: spacing[3], right: spacing[3] }} />
      )}
    </label>
  );
}

/** Tres barras de nivel, como en el deck (las activas con el color del plan). */
function NivelBarras({ nivel, color }) {
  return (
    <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "flex-end", gap: spacing[1], height: 14 }}>
      {[42, 71, 100].map((alto, i) => (
        <span key={alto} style={{ width: 5, height: `${alto}%`, borderRadius: radius.full, background: i < nivel ? color : colors.gray200 }} />
      ))}
    </span>
  );
}

/**
 * Contador con botones +/- e input numérico editable (mismo patrón que el
 * Cotizador). Si el ancho no alcanza para el rótulo y los controles en una
 * línea, los controles bajan abajo en vez de aplastar el rótulo.
 */
function Stepper({ id, icon: Icon, label, hint, value, onChange, required, width = 88 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: spacing[3], rowGap: spacing[2], padding: `${spacing[2]}px 0` }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flex: "1 1 180px", minWidth: 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: radius.sm, background: colors.primaryLighter, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={15} color={colors.primary} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor={id} style={{ fontSize: typography.sizes.md, color: colors.gray900, display: "block" }}>
            {label}{required && <span style={{ color: colors.error }} aria-hidden="true"> *</span>}
          </label>
          {hint && <div style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>{hint}</div>}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginLeft: "auto" }}>
        <button type="button" aria-label={`Restar ${label}`} style={stepBtn} className="habitia-btn-secondary" onClick={() => onChange(value - 1)} disabled={value <= 0}>
          <FaMinus size={10} />
        </button>
        <input
          id={id} type="number" min="0" step="1" inputMode="numeric" className="habitia-input" aria-required={required || undefined}
          style={{ ...inputBase, width, textAlign: "center", padding: "8px 4px", boxSizing: "border-box" }}
          value={value} onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" aria-label={`Sumar ${label}`} style={stepBtn} className="habitia-btn-secondary" onClick={() => onChange(value + 1)}>
          <FaPlus size={10} />
        </button>
      </div>
    </div>
  );
}

function Linea({ label, valor, total }) {
  return (
    <div
      style={{
        display: "flex", justifyContent: "space-between", gap: spacing[3], fontSize: typography.sizes.base,
        ...(total ? { borderTop: `1px solid ${colors.gray100}`, paddingTop: spacing[2], marginTop: spacing[1] } : {}),
      }}
    >
      <span style={{ color: total ? colors.gray900 : colors.gray600 }}>{label}</span>
      <span style={{ color: colors.gray900, whiteSpace: "nowrap" }}>{valor}</span>
    </div>
  );
}

function InfoPill({ bg, color, children }) {
  return (
    <div style={{ background: bg, color, borderRadius: radius.sm, padding: `${spacing[2]}px ${spacing[3]}px`, fontSize: typography.sizes.base, lineHeight: 1.4 }}>
      {children}
    </div>
  );
}

/**
 * Las hojas del PDF a escala para que entren a lo ancho del modal. Es otra
 * instancia del template (la que se captura es la offscreen, a tamaño real):
 * se mide el alto natural para darle a la caja el alto ya escalado.
 */
function VistaPreviaEscalada({ data }) {
  const cajaRef = useRef(null);
  const hojasRef = useRef(null);
  const [ancho, setAncho] = useState(0);
  const [alto, setAlto] = useState(0);

  useEffect(() => {
    const caja = cajaRef.current;
    const hojas = hojasRef.current;
    if (!caja || !hojas) return undefined;
    const medir = () => {
      setAncho(caja.clientWidth);
      setAlto(hojas.offsetHeight);
    };
    medir();
    // jsdom (tests) no tiene ResizeObserver.
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(medir);
    ro.observe(caja);
    ro.observe(hojas);
    return () => ro.disconnect();
  }, []);

  const escala = ancho > 0 ? Math.min(1, ancho / A4_W) : 0.9;
  const altoNatural = alto > 0 ? alto : A4_H * 5;
  return (
    <div ref={cajaRef} style={{ width: "100%" }}>
      <div style={{ width: A4_W * escala, height: altoNatural * escala, margin: "0 auto", overflow: "hidden", borderRadius: radius.sm }}>
        <div ref={hojasRef} style={{ width: A4_W, transform: `scale(${escala})`, transformOrigin: "top left" }}>
          <TemplateBO data={data} />
        </div>
      </div>
    </div>
  );
}

const inputStyle = { ...inputBase, boxSizing: "border-box" };

// Mismo estilo que el label de FormField (para el label con acción al lado).
const labelStyle = {
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray900,
  lineHeight: 1.4,
};

const sectionTitle = {
  fontSize: typography.sizes.lg,
  fontWeight: 400,
  color: colors.black,
  margin: `0 0 ${spacing[3]}px`,
};

const textoSecundario = {
  fontSize: typography.sizes.base,
  color: colors.gray600,
  lineHeight: 1.5,
};

const hintStyle = {
  fontSize: typography.sizes.sm,
  color: colors.gray400,
  margin: 0,
  lineHeight: 1.4,
};

const checkLabel = {
  display: "flex",
  alignItems: "center",
  gap: spacing[2],
  cursor: "pointer",
  fontSize: typography.sizes.md,
  color: colors.gray900,
};

const checkInput = {
  width: 16,
  height: 16,
  margin: 0,
  flexShrink: 0,
  accentColor: colors.primary,
};

const btnChico = { ...btnSecondary, padding: "8px 16px", fontSize: typography.sizes.base };

const linkBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing[1],
  padding: 0,
  border: "none",
  background: "transparent",
  color: colors.primary,
  cursor: "pointer",
  fontSize: typography.sizes.sm,
  fontFamily: typography.fontFamily,
  transition: `all ${transitions.fast}`,
};

const fieldsetReset = { border: 0, margin: 0, padding: 0, minWidth: 0 };

// Oculto a la vista pero accesible (el radio sigue siendo navegable con teclado).
const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const stepBtn = {
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: colors.white,
  color: colors.gray900,
  border: `1px solid ${colors.gray200}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  transition: `all ${transitions.fast}`,
  padding: 0,
};
